import React, { useState, useRef, useEffect, useCallback } from "react";
import MerakiSDK, { Exceptions } from "@urbanpiper-engineering/meraki-sdk";
import { useTranslation } from "react-i18next";
import { getStaticProps as getInitialProps } from "../../hooks/server.props";
import { Wrapper } from "@googlemaps/react-wrapper";
import {
  StoreLocatorCardMobile,
  Drawer,
  Divider,
  StoreLocatorSearch,
  GoogleMap,
  Typography,
  StoreLocatorList,
} from "@urbanpiper-engineering/meraki-components";
import useConfirm from "@urbanpiper-engineering/meraki-components/dist/Molecules/Confirm";
import debounce from "../../external/helpers/debounce";
import useAlert from "@urbanpiper-engineering/meraki-components/dist/Molecules/Alert";
import configSelector from "../../external/configSelector";
import { StoreState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/stores/states/store.state";
import { useRouter } from "next/router";
import pageSelector from "../../external/pageSelector";
import SeoData from "../../components/seo_data";
import CrossIcon from "../../assets/icons/cross-icon";
import useFetchGoogleMapKey from "../../hooks/useFetchGoogleMapKey";

import styles from "./stores.module.scss";

export const getStaticProps = async (props: any) => {
  const initProps = await getInitialProps(props);
  const mapKey = configSelector.getGoogleMapsKey() ?? null;
  const seoConfig = pageSelector.getStoreLocatorPage()?.seo[props.locale] || {};
  const primaryColor = configSelector.getPrimaryColor();

  const primaryDomain = configSelector.getPrimaryDomain();
  const openGraphData = {
    title: seoConfig?.title || null,
    description: seoConfig?.description || null,
    url:
      primaryDomain?.url?.length > 0
        ? `https://${primaryDomain?.url}/store-locator`
        : null,
    image: null,
  };

  return {
    props: {
      ...initProps.props,
      mapKey,
      seoConfig,
      openGraphData,
      primaryColor,
    },
  };
};

function StoresPage({
  mapKey,
  seoConfig,
  openGraphData,
  primaryColor,
  businessInfo,
}: any) {
  const { t } = useTranslation();
  const router = useRouter();
  const [storeList, setStoreList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const useStoreState = MerakiSDK.getStoreState().hook();
  const confirmDialog = useConfirm();
  const alertDialog = useAlert();
  const selectedStore = useStoreState(
    (state: typeof StoreState) => state.selectedStore,
  );
  const [isStoreDrawerVisible, setIsStoreDrawerVisible] =
    useState<boolean>(false);
  const [filteredListMobile, setFilteredListMobile] = useState<any>([]);
  const [filteredListWeb, setFilteredListWeb] = useState<any>(storeList);
  const searchRef = useRef<HTMLDivElement>(null);
  const [keyPresent, setKeyPresent] = useState(false);
  const [defaultMapkey, setDefaultMapKey] = useState("");
  const googleApiKey = mapKey || configSelector.getDefaultMapsKey();
  const fetchMapKey = useFetchGoogleMapKey(googleApiKey);
  const [hasMoreStores, setHasMoreStores] = useState(false);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchMapKey(setDefaultMapKey, setKeyPresent);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        await getStores();
      } catch (error) {
        console.error("Store Locator getStores Failed: ", error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page, searchText]);

  const getStores = async () => {
    const { hasMore, stores } = await MerakiSDK.getStores(page, 50, searchText);
    setHasMoreStores(hasMore);
    const storesEnabledForOrdering = stores?.filter(
      (store: any) => store?.enabled_for_ordering,
    );

    if (page === 0 || searchText === "") {
      setStoreList(storesEnabledForOrdering);
      setFilteredListWeb(storesEnabledForOrdering);
    } else {
      setStoreList([...storeList, ...storesEnabledForOrdering]);
      setFilteredListWeb([...filteredListWeb, ...storesEnabledForOrdering]);
    }
  };

  const handleSearchStore = useCallback(
    debounce(
      async (searchTerm: string) => {
        if (searchTerm.length < 3 && searchTerm.length !== 0) return;
        setPage(0);
        setSearchText(searchTerm);
      },
      1000,
      { leading: false },
    ) as Function,
    [],
  );

  const handlePageUpdate = () => {
    setPage((prev) => prev + 1);
  };

  const handleSelectStoreError = (err: any) => {
    if (err.message === Exceptions.STORE_ORDERING_NOT_ENABLED) {
      alertDialog({
        title: t("storeLocator.storeClosed"),
        content:
          businessInfo?.msg_nearest_store_closed ||
          t("storeLocator.storeClosed"),
      });
    }
    if (err.message === Exceptions.STORE_TEMPORARY_CLOSED) {
      alertDialog({
        title: t("storeLocator.storeClosed"),
        content:
          businessInfo?.msg_nearest_store_closed ||
          t("storeLocator.storeClosed"),
      });
    }
  };

  const handleSelectStore = (storeId: number) => {
    const selectedStore = storeList.find((s: any) => s.id === storeId);
    if (selectedStore) {
      const storeContent = {
        id: storeId,
        address: selectedStore.address,
        latitude: selectedStore.lat,
        longitude: selectedStore.lng,
        storeInfo: selectedStore,
        ffMode: "pickup",
      };

      try {
        MerakiSDK.setSelectedStore(storeContent);
        router.push("/menu");
      } catch (err: any) {
        if (err.message === Exceptions.CART_ALREADY_EXISTS) {
          confirmDialog({
            title: t("header.clearCartHeading"),
            content: t("header.clearCartDescription"),
            onConfirm: () => {
              try {
                MerakiSDK.setSelectedStore(storeContent, true);
                router.push("/menu");
              } catch (err: any) {
                handleSelectStoreError(err);
              }
            },
          });
        }
        handleSelectStoreError(err);
      }
    }
  };

  return (
    <div className={styles.parent}>
      <SeoData seoData={seoConfig} openGraphData={openGraphData} />
      <h1 className="visually-hidden">{seoConfig?.title || "Store Locator"}</h1>
      <div className={`container ${styles.web_container}`}>
        <div className={styles.map_main}>
          {keyPresent && (
            <Wrapper apiKey={mapKey || defaultMapkey}>
              {(selectedStore || storeList?.[0]) && (
                <GoogleMap
                  className={styles.map}
                  center={{
                    lat: selectedStore?.latitude || storeList?.[0]?.lat,
                    lng: selectedStore?.longitude || storeList?.[0]?.lng,
                  }}
                  zoom={13}
                  markers={storeList.map((store: any) => ({
                    lat: store.lat,
                    lng: store.lng,
                    label: store.name,
                    color: primaryColor,
                  }))}
                />
              )}
            </Wrapper>
          )}
        </div>
        <div className={styles.stores}>
          <Typography variant="headingOne" classes={styles.typo}>
            {t("storeLocator.sectionHeading")}
          </Typography>
          <StoreLocatorList
            handlePageUpdate={handlePageUpdate}
            hasMoreStores={hasMoreStores}
            loading={isLoading}
            classes={styles.web_store_list}
            stores={filteredListWeb}
            searchStore={handleSearchStore}
            selectStore={(storeId: number) => handleSelectStore(storeId)}
          />
        </div>
      </div>
      <div className={styles.mobile_container}>
        {keyPresent && (
          <Wrapper apiKey={mapKey || defaultMapkey}>
            <GoogleMap
              className={styles.mobile_map}
              center={{
                lat: selectedStore?.latitude || storeList?.[0]?.latitude,
                lng: selectedStore?.longitude || storeList?.[0]?.longitude,
              }}
              zoom={13}
              markers={storeList.map((store: any) => ({
                lat: store.lat,
                lng: store.lng,
                label: store.name,
                color: primaryColor,
              }))}
            />
          </Wrapper>
        )}

        <div ref={searchRef}>
          <StoreLocatorSearch
            searchStore={handleSearchStore}
            selectStore={(storeId: number) => handleSelectStore(storeId)}
            defaultSuggestion={storeList?.[0]}
            stores={filteredListMobile.length ? filteredListMobile : storeList}
          />
        </div>
      </div>
      <Drawer
        classes={styles.mobile_stores_drawer}
        open={isStoreDrawerVisible}
        direction={"right"}
        onClose={() => setIsStoreDrawerVisible(false)}
      >
        <div className={styles.mobile_stores_drawer_header}>
          <Typography
            variant="headingTwo"
            classes={styles.mobile_stores_drawer_header_title}
          >
            {t("storeLocator.sectionHeading")}
          </Typography>
          <CrossIcon
            size={{ width: 12, heigth: 12 }}
            onClickCallback={() => setIsStoreDrawerVisible(false)}
          />
        </div>
        {storeList.map((store: any) => (
          <React.Fragment key={store.id}>
            <StoreLocatorCardMobile
              key={store.id}
              {...store}
              selectStore={() => {
                MerakiSDK.setSelectedStore(
                  {
                    id: store.id,
                    address: store.address,
                    latitude: store.lat,
                    longitude: store.lng,
                    ffMode: "pickup",
                  },
                  true,
                );
                router.push("/menu");
              }}
            />
            <Divider
              dividerStyle={"solid"}
              variant={"horizontal"}
              size={"100%"}
            />
          </React.Fragment>
        ))}
      </Drawer>
    </div>
  );
}

export default StoresPage;
